import { create } from 'zustand';
import type { Appointment } from '@/types/appointments';
import { apiService } from '@/services/ApiService';
import { AppointmentApiError } from '@/types/appointments';

type AppointmentHistoryState = {
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  totalItems: number;
  items: Appointment[];
  lastDietitianId?: string;
  lastPatientId?: string;
};

type AppointmentHistoryActions = {
  load: (dietitianId: string, patientId: string, page?: number, pageSize?: number) => Promise<void>;
  refresh: () => Promise<void>;
  reset: () => void;
};

const initialState: AppointmentHistoryState = {
  loading: false,
  error: null,
  page: 1,
  pageSize: 10,
  totalItems: 0,
  items: [],
  lastDietitianId: undefined,
  lastPatientId: undefined,
};

export const useAppointmentHistoryStore = create<AppointmentHistoryState & AppointmentHistoryActions>((set, get) => ({
  ...initialState,

  load: async (dietitianId, patientId, page = 1, pageSize = get().pageSize) => {
    set((state) => ({
      ...state,
      loading: true,
      error: null,
      lastDietitianId: dietitianId,
      lastPatientId: patientId,
      pageSize,
    }));

    try {
      const result = await apiService.appointment.listAppointments({ dietitianId, patientId, page, pageSize });

      set((state) => ({
        ...state,
        loading: false,
        error: null,
        page: result.page,
        pageSize: result.pageSize,
        totalItems: result.totalItems,
        items: result.items,
      }));
    } catch (err) {
      const message =
        err instanceof AppointmentApiError
          ? err.message
          : 'Unable to load appointments. Please try again.';
      set((state) => ({
        ...state,
        loading: false,
        error: message,
        items: [],
        totalItems: 0,
      }));
    }
  },

  refresh: async () => {
    const { lastDietitianId, lastPatientId, page, pageSize, load } = get();
    if (!lastDietitianId || !lastPatientId) return;
    await load(lastDietitianId, lastPatientId, page, pageSize);
  },

  reset: () => set(() => ({ ...initialState })),
}));
